import {
  getElecAnalysisFutureProdComparisonTable,
  getElecAnalysisFutureProdDaily,
  getElecAnalysisFutureProductsChart,
} from "$/api/market-monitoring/electricity-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import { GraphIndicators } from "$/lib/components/graphs/GraphIndicator/GraphIndicators";
import LineGraph from "$/lib/components/graphs/LineGraph";
import { Tabs, TextInput } from "$/lib/components/molecules";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { CheckboxSet } from "$/lib/components/molecules/ChecboxSet";
import { displayMessage } from "$/lib/components/molecules/CustomToast/CustomToast.utils";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import TableGraphWidget from "$/lib/components/widgets/TableGraphWidget";
import { RenderTableNumberCell } from "$/lib/components/widgets/TableGraphWidget/RenderTableNumberCell";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { FutureProdComparisonTableType } from "$/lib/types/api/market-monitoring/electricity-analysis.types";
import type { CellType, ColumnType } from "$/lib/types/Graphs.types";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import { DEFAULT_GRAPH_TABS } from "$/lib/utils/constants/marketMonitoring/graphs.constants";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  BASE_ELEC_COLORS,
  handleOnChangeDefaultGraphsTabs,
  PEAK_ELEC_COLORS,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

// const BASE_COLORS = ["#273c7b", "#6480c2", "#8dbdaf", "#273c7b50"];
// const PEAK_COLORS = ["#784b7a", "#ad79a2", "#f49ba7", "#784b7a80"];

// const COLORS = [...BASE_COLORS, ...PEAK_COLORS];

type Props = {
  selectedDate: Date;
} & CustomDashboardProps;

const ElecAnalysisFutureProductsChart = ({
  selectedDate,
  id,
  isDraggable = false,
  isShowcase = false,
}: Props) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [filters, setFilters] = useState<{
    base: boolean;
    peak: boolean;
  }>({
    base: true,
    peak: false,
  });

  const [indices, setIndices] = useState<
    | {
        start: number;
        end: number;
      }
    | undefined
  >();

  const [amplitude, setAmplitude] = useState<number>(20);

  const [tablePeriod, setTablePeriod] = useState<FutureProductPeriod | "DAY">(
    "QUARTER",
  );

  const { data, isLoading } = useQuery({
    queryKey: ["get-elec-analysis-future-prod-chart", dateRange, amplitude],
    queryFn: () =>
      getElecAnalysisFutureProductsChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        base: filters.base,
        peak: filters.peak,
        amplitude: amplitude,
      }),
  });

  const { data: tableData } = useQuery({
    queryKey: [
      "get-elec-analysis-future-prod-comparison-table",
      dateRange,
      tablePeriod,
    ],
    queryFn: () =>
      getElecAnalysisFutureProdComparisonTable({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        period: tablePeriod,
      }),
  });

  const { data: daily } = useQuery({
    queryKey: ["get-elec-analysis-future-prod-daily", selectedDate],
    queryFn: () =>
      getElecAnalysisFutureProdDaily({ date: getDate(selectedDate) }),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.base && !filters.peak
          ? data.baseProductsData
          : filters.peak && !filters.base
            ? data.peakProductsData
            : filters.base && filters.peak
              ? data.baseAndPeakData
              : []
        : [],
    [data, filters],
  );
  const toDisplayVolatility = useMemo(
    () =>
      data
        ? filters.base && !filters.peak
          ? data.baseProductsVolatilityData
          : filters.peak && !filters.base
            ? data.peakProductsVolatilityData
            : filters.base && filters.peak
              ? data.baseAndPeakVolatilityData
              : []
        : [],
    [data, filters],
  );

  const colorsToDisplay = useMemo(() => {
    if (filters.base && filters.peak) {
      return [...BASE_ELEC_COLORS, ...PEAK_ELEC_COLORS];
    }

    return filters.base ? BASE_ELEC_COLORS : PEAK_ELEC_COLORS;
  }, [filters]);

  const baseKeys = useMemo(
    () =>
      getArrayKeys(data?.baseProductsData ?? []).filter(
        (key) => !key.includes("MM"),
      ),
    [data],
  );
  const peakKeys = useMemo(
    () =>
      getArrayKeys(data?.peakProductsData ?? []).filter(
        (key) => !key.includes("MM"),
      ),
    [data],
  );

  const keys = useMemo(
    () =>
      filters.base && filters.peak
        ? [...baseKeys, ...peakKeys]
        : filters.base
          ? baseKeys
          : filters.peak
            ? peakKeys
            : [],
    [filters, baseKeys, peakKeys],
  );

  const avgKeys = useMemo(
    () => getArrayKeys(toDisplay).filter((key) => key.includes("MM")),
    [toDisplay],
  );

  const extraKeys = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]: { dataKey: avgKeys[i] },
        }),
        {},
      ),
    [avgKeys, keys],
  );

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = keys.reduce(
    (acc, key, i) => ({
      ...acc,
      [key]: colorsToDisplay[i],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  const baseTableKeys = ["cal1BaseAvg", "cal2BaseAvg", "cal3BaseAvg"];
  const peakTableKeys = ["cal1PeakAvg", "cal2PeakAvg", "cal3PeakAvg"];
  const allTableKeys = [...baseTableKeys, ...peakTableKeys];

  const tableBounds = useMemo(() => {
    if (tableData) {
      const keysToCompare =
        filters.base && filters.peak
          ? allTableKeys
          : filters.base
            ? baseTableKeys
            : peakTableKeys;

      return getMinMax(tableData[tablePeriod], keysToCompare);
    }

    return { min: { value: 0 }, max: { value: 0 } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, tablePeriod, filters]);

  const columns: ColumnType<FutureProdComparisonTableType>[] = useMemo(() => {
    const firstHeaders: ColumnType<FutureProdComparisonTableType>[] = [
      {
        header: () => {},
        accessorKey: "year",
        cell: (cell) => {
          const values = cell.row.original;

          const display =
            tablePeriod === "QUARTER" || tablePeriod === "MONTH"
              ? [values.quarter, values.month]
              : [values.month, values.day];

          return (
            <Flexbox className="gap-0.5 whitespace-nowrap">
              <span className="text-xs font-medium text-black">
                {cell.getValue()}
              </span>

              <span className="text-[10px] text-tertiary-600">
                {display
                  .filter((v) => v)
                  .join(tablePeriod === "DAY" ? " " : " - ")}
              </span>
            </Flexbox>
          );
        },
      },
    ];

    return [
      ...firstHeaders,
      ...(filters.base
        ? [
            {
              header: "Baseload Cal +1",
              accessorKey: "cal1BaseAvg",
              cell: (cell: CellType<FutureProdComparisonTableType>) => (
                <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
              ),
            },
            {
              header: "Baseload Cal +2",
              accessorKey: "cal2BaseAvg",
              cell: (cell: CellType<FutureProdComparisonTableType>) => (
                <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
              ),
            },
            {
              header: "Baseload Cal +3",
              accessorKey: "cal3BaseAvg",
              cell: (cell: CellType<FutureProdComparisonTableType>) => (
                <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
              ),
            },
          ]
        : []),
      ...(filters.peak
        ? [
            {
              header: "Peakload Cal +1",
              accessorKey: "cal1PeakAvg",
              cell: (cell: CellType<FutureProdComparisonTableType>) => (
                <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
              ),
            },
            {
              header: "Peakload Cal +2",
              accessorKey: "cal2PeakAvg",
              cell: (cell: CellType<FutureProdComparisonTableType>) => (
                <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
              ),
            },
            {
              header: "Peakload Cal +3",
              accessorKey: "cal3PeakAvg",
              cell: (cell: CellType<FutureProdComparisonTableType>) => (
                <RenderTableNumberCell tableBounds={tableBounds} {...cell} />
              ),
            },
          ]
        : []),
    ];
  }, [filters, tableBounds, tablePeriod]);

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color: colorsToDisplay[i],
      })),
    );
  }, [keys, colorsToDisplay]);

  useEffect(() => {
    setIndices(undefined);
  }, [dateRange]);

  return (
    <DisplayBox
      title="Calendaires et Moyennes Mobiles"
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      MiddleComponent={
        <Tabs
          className="mx-auto"
          type="bordered"
          tabs={DEFAULT_GRAPH_TABS}
          activeTab={selectedGraphTabId}
          handleTabChange={setSelectedGraphTabId}
        />
      }
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <Flexbox fullWidth className="h-[65%]">
              <LineGraph
                data={toDisplay}
                graphVisibility={graphVisibility}
                title="Prix Stlt EEX"
                xAxisDataKey="date"
                syncId="id-elec-analysis-future-prod"
                colors={colors}
                theme="blue"
                extraKeys={extraKeys}
                keys={keys}
                heightPercent={65}
                leftMargin={-10}
                indices={indices}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-[35%]">
              <LineGraph
                data={toDisplayVolatility}
                title="Volatilité"
                graphVisibility={graphVisibility}
                syncId="id-elec-analysis-future-prod"
                withBrush
                xAxisDataKey="date"
                colors={colors}
                theme="blue"
                keys={keys}
                heightPercent={40}
                yAxisTicksNumber={7}
                leftMargin={-10}
                indices={indices}
                setIndices={setIndices}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
          />

          <CheckboxSet<"base" | "peak">
            id="elec-base"
            title="Base / Peak"
            selected={filters}
            onChange={(id, value) =>
              setFilters((prev) => {
                const newValue = { ...prev, [id]: value };
                if (!newValue.base && !newValue.peak) return prev;
                return newValue;
              })
            }
            options={[
              { id: "base", label: "Base" },
              { id: "peak", label: "Peak" },
            ]}
            row
          />

          <TextInput
            label="Amplitude moyenne mob."
            className="w-[100px]"
            labelClassName="whitespace-nowrap text-base font-semibold text-secondary"
            value={amplitude}
            type="number"
            onChange={(e) => {
              const value = +e.target.value;

              if (value >= 0 && value <= 50) {
                setAmplitude(+e.target.value);

                return;
              }

              displayMessage(
                "L'amplitude doit être comprise entre 0 et 50",
                "warning",
              );
            }}
          />

          <Flexbox fullHeight className="gap-4 overflow-y-auto pb-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />

            {daily && !!daily.length && (
              <Flexbox fullWidth>
                <GraphIndicators
                  selectedDate={selectedDate}
                  data={daily ?? []}
                  indicatorsTitles={{
                    title1: "Base",
                    title2: "Peak",
                  }}
                />
              </Flexbox>
            )}

            <Flexbox fullWidth className="p-1">
              <TableGraphWidget<FutureProdComparisonTableType>
                columns={columns}
                data={tableData ? tableData[tablePeriod] : []}
                tableBounds={tableBounds}
                selectedPeriod={tablePeriod}
                setSelectedPeriod={setTablePeriod}
              />
            </Flexbox>
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default ElecAnalysisFutureProductsChart;
